import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserCheck, XCircle, Pause, Ban, RotateCcw, Key } from "lucide-react";
import { getUser, roleDisplayLabel } from "@/lib/auth";

const ROLES_ALL = ["Promoter", "Supervisor", "Admin", "Coca-Cola", "IT", "Project Lead"];
const ROLES_PROJECT_LEAD = ["Promoter"];  // Item 6 — Project Lead may approve only as Promoter

export default function AdminApprovals() {
  const me = getUser();
  const isProjectLead = me?.role === "Project Lead";
  const ROLES = isProjectLead ? ROLES_PROJECT_LEAD : ROLES_ALL;
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [stores, setStores] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);   // user obj
  const [rejecting, setRejecting] = useState(null);
  const [suspending, setSuspending] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const [reason, setReason] = useState("");
  const [approvalForm, setApprovalForm] = useState({});
  const [pwShown, setPwShown] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s, p] = await Promise.all([
        api.get(`/admin/signups?status=${tab}`),
        api.get("/stores"),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setRows(r.data || []);
      setStores(s.data || []);
      setProjects(p.data || []);
    } catch { toast.error("Failed to load sign-ups"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const openApprove = (u) => {
    setApprovalForm({
      role: "Promoter",   // default — safe for both Admin and Project Lead approvers
      project_id: projects[0]?.id || "",
      assigned_store: "",
      assigned_stores_list: [],
      monthly_salary: 0,
      login_id: "",
    });
    setApproving(u);
  };

  const doApprove = async () => {
    if (!approvalForm.role) return toast.error("Choose a role");
    if (approvalForm.role === "Promoter" && !approvalForm.assigned_store) return toast.error("Choose an assigned store for the promoter");
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/signups/${approving.id}/approve`, approvalForm);
      toast.success(`Approved. Login ID: ${data.login_id}`);
      setPwShown({ name: approving.name, phone: approving.phone, login_id: data.login_id });
      setApproving(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Approval failed"); }
    finally { setSaving(false); }
  };

  const doReject = async () => {
    if (!reason.trim()) return toast.error("Rejection reason is mandatory");
    setSaving(true);
    try {
      await api.post(`/admin/signups/${rejecting.id}/reject`, { reason: reason.trim() });
      toast.success("Sign-up rejected");
      setRejecting(null); setReason(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Reject failed"); }
    finally { setSaving(false); }
  };

  const doSuspend = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/signups/${suspending.id}/suspend`, { reason: reason.trim() || "Suspended by admin" });
      toast.success("User suspended"); setSuspending(null); setReason(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Suspend failed"); }
    finally { setSaving(false); }
  };

  const doRevoke = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/signups/${revoking.id}/revoke`, { reason: reason.trim() || "Access revoked by admin" });
      toast.success("Access revoked"); setRevoking(null); setReason(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Revoke failed"); }
    finally { setSaving(false); }
  };

  const doReactivate = async (u) => {
    if (!window.confirm(`Reactivate ${u.name}?`)) return;
    try {
      await api.post(`/admin/signups/${u.id}/reactivate`);
      toast.success("Reactivated"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Reactivate failed"); }
  };

  const doResetPw = async (u) => {
    if (!window.confirm(`Reset password for ${u.name}? This will log them out of all sessions.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setPwShown({ name: u.name, phone: u.phone, password: data.new_password });
    } catch (err) { toast.error(err.response?.data?.detail || "Reset failed"); }
  };

  const pendingCount = useMemo(() => rows.filter(r => r.status === "pending").length, [rows]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader />
      <div className="flex flex-1">
        <SideNav />
        <main className="flex-1 p-4 md:p-8 max-w-6xl mx-auto w-full">
          <div className="flex items-center gap-3 mb-6">
            <UserCheck className="h-6 w-6 text-brand-red" />
            <h1 className="font-display text-2xl md:text-3xl font-bold" data-testid="approvals-title">Sign-up Approvals</h1>
            {tab === "pending" && pendingCount > 0 && (
              <Badge data-testid="approvals-pending-badge" className="bg-brand-red text-white">{pendingCount} pending</Badge>
            )}
          </div>

          <Tabs value={tab} onValueChange={setTab} className="mb-4">
            <TabsList data-testid="approvals-tabs">
              <TabsTrigger value="pending" data-testid="tab-pending">Pending</TabsTrigger>
              <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
              <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
              <TabsTrigger value="suspended" data-testid="tab-suspended">Suspended</TabsTrigger>
              <TabsTrigger value="revoked" data-testid="tab-revoked">Revoked</TabsTrigger>
            </TabsList>
          </Tabs>

          <Card className="p-0 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-8 text-center text-gray-500" data-testid="approvals-empty">No {tab} sign-ups</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="p-3 text-left">Name</th>
                      <th className="p-3 text-left">Email</th>
                      <th className="p-3 text-left">Phone</th>
                      <th className="p-3 text-left">Requested</th>
                      <th className="p-3 text-left">Role</th>
                      <th className="p-3 text-left">Note</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(u => (
                      <tr key={u.id} className="border-t border-gray-100" data-testid={`signup-row-${u.id}`}>
                        <td className="p-3 font-medium">{u.name}</td>
                        <td className="p-3 text-gray-600">{u.email}</td>
                        <td className="p-3 font-mono">{u.phone}</td>
                        <td className="p-3 text-gray-500 text-xs">{(u.signup_at || "").slice(0, 10)}</td>
                        <td className="p-3">{u.role ? roleDisplayLabel(u.role) : "—"}</td>
                        <td className="p-3 text-gray-500 text-xs">
                          {u.reject_reason && <span>Rejected: {u.reject_reason}</span>}
                          {u.suspend_reason && <span>Suspended: {u.suspend_reason}</span>}
                          {u.revoke_reason && <span>Revoked: {u.revoke_reason}</span>}
                        </td>
                        <td className="p-3 text-right space-x-2 whitespace-nowrap">
                          {tab === "pending" && (
                            <>
                              <Button size="sm" data-testid={`approve-btn-${u.id}`} className="bg-green-600 hover:bg-green-700" onClick={() => openApprove(u)}>
                                <UserCheck className="h-3.5 w-3.5 mr-1" /> Approve
                              </Button>
                              <Button size="sm" variant="destructive" data-testid={`reject-btn-${u.id}`} onClick={() => { setRejecting(u); setReason(""); }}>
                                <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                              </Button>
                            </>
                          )}
                          {tab === "approved" && (
                            <>
                              <Button size="sm" variant="outline" data-testid={`suspend-btn-${u.id}`} onClick={() => { setSuspending(u); setReason(""); }}>
                                <Pause className="h-3.5 w-3.5 mr-1" /> Suspend
                              </Button>
                              <Button size="sm" variant="destructive" data-testid={`revoke-btn-${u.id}`} onClick={() => { setRevoking(u); setReason(""); }}>
                                <Ban className="h-3.5 w-3.5 mr-1" /> Revoke
                              </Button>
                              <Button size="sm" variant="outline" data-testid={`reset-pw-btn-${u.id}`} onClick={() => doResetPw(u)}>
                                <Key className="h-3.5 w-3.5 mr-1" /> Reset pw
                              </Button>
                            </>
                          )}
                          {(tab === "suspended" || tab === "revoked") && (
                            <Button size="sm" data-testid={`reactivate-btn-${u.id}`} onClick={() => doReactivate(u)}>
                              <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reactivate
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </main>
      </div>

      {/* Approve dialog */}
      <Dialog open={!!approving} onOpenChange={(o) => !o && setApproving(null)}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader><DialogTitle>Approve {approving?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Assign Role*</label>
              <Select value={approvalForm.role} onValueChange={(v) => setApprovalForm(f => ({ ...f, role: v }))}>
                <SelectTrigger data-testid="approve-role-select" className="h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{roleDisplayLabel(r)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {projects.length > 1 && (
              <div>
                <label className="text-xs font-semibold text-gray-700">Assign Project</label>
                <Select value={approvalForm.project_id} onValueChange={(v) => setApprovalForm(f => ({ ...f, project_id: v }))}>
                  <SelectTrigger data-testid="approve-project-select" className="h-11 mt-1"><SelectValue placeholder="Default project" /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {approvalForm.role === "Promoter" && (
              <>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Assigned Store*</label>
                  <Select value={approvalForm.assigned_store} onValueChange={(v) => setApprovalForm(f => ({ ...f, assigned_store: v }))}>
                    <SelectTrigger data-testid="approve-store-select" className="h-11 mt-1"><SelectValue placeholder="Choose store" /></SelectTrigger>
                    <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.store_code} — {s.store_name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Monthly Salary (₹)</label>
                  <Input data-testid="approve-salary-input" type="number" className="h-11 mt-1"
                    value={approvalForm.monthly_salary}
                    onChange={(e) => setApprovalForm(f => ({ ...f, monthly_salary: parseFloat(e.target.value) || 0 }))} />
                </div>
              </>
            )}
            <div>
              <label className="text-xs font-semibold text-gray-700">Login ID (optional — auto-generated if blank)</label>
              <Input data-testid="approve-login-id-input" className="h-11 mt-1" value={approvalForm.login_id}
                onChange={(e) => setApprovalForm(f => ({ ...f, login_id: e.target.value.trim() }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproving(null)}>Cancel</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={doApprove} disabled={saving} data-testid="approve-confirm-btn">
              {saving ? "Approving…" : "Approve"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader><DialogTitle>Reject {rejecting?.name}</DialogTitle></DialogHeader>
          <label className="text-xs font-semibold text-gray-700">Reason* (shown at login)</label>
          <Input data-testid="reject-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for rejection" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejecting(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doReject} disabled={saving} data-testid="reject-confirm-btn">
              {saving ? "Rejecting…" : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Suspend dialog */}
      <Dialog open={!!suspending} onOpenChange={(o) => !o && setSuspending(null)}>
        <DialogContent data-testid="suspend-dialog">
          <DialogHeader><DialogTitle>Suspend {suspending?.name}</DialogTitle></DialogHeader>
          <label className="text-xs font-semibold text-gray-700">Reason (optional)</label>
          <Input data-testid="suspend-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSuspending(null)}>Cancel</Button>
            <Button onClick={doSuspend} disabled={saving} data-testid="suspend-confirm-btn">Suspend</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke dialog */}
      <Dialog open={!!revoking} onOpenChange={(o) => !o && setRevoking(null)}>
        <DialogContent data-testid="revoke-dialog">
          <DialogHeader><DialogTitle>Revoke access for {revoking?.name}</DialogTitle></DialogHeader>
          <label className="text-xs font-semibold text-gray-700">Reason (optional)</label>
          <Input data-testid="revoke-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevoking(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doRevoke} disabled={saving} data-testid="revoke-confirm-btn">Revoke</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Credential toast */}
      <Dialog open={!!pwShown} onOpenChange={(o) => !o && setPwShown(null)}>
        <DialogContent data-testid="credentials-dialog">
          <DialogHeader><DialogTitle>Credentials for {pwShown?.name}</DialogTitle></DialogHeader>
          <div className="text-sm space-y-2">
            <p>Share these securely with the user:</p>
            <div className="bg-gray-50 rounded-lg p-3 font-mono text-sm space-y-1">
              {pwShown?.login_id && <div><span className="text-gray-500">Login ID:</span> {pwShown.login_id}</div>}
              <div><span className="text-gray-500">Phone:</span> {pwShown?.phone}</div>
              {pwShown?.password && <div><span className="text-gray-500">Password:</span> {pwShown.password}</div>}
              {!pwShown?.password && <div className="text-gray-500 text-xs">The user's chosen password is unchanged and remains hidden. Use Reset Password to generate a new one.</div>}
            </div>
          </div>
          <DialogFooter><Button onClick={() => setPwShown(null)} data-testid="credentials-close-btn">Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
